/**
 * Small inked marks drawn around Merge jar bodies, baked once through the art registry and
 * drawn as sprites (Pixi) or images (Canvas 2D), scaled to the body:
 *   jar/ring-<tone>-<s|m|l>   overflow rim ring (warn orange, alert red): a coloured band with
 *                             ink on both edges and a glint, in three line weights so the ring
 *                             reads the same on a phone cell and on a dragon;
 *   jar/badge-<tone>          the bouncing "!" warning badge;
 *   jar/ghost-<s|m|l>         shrink ghost: white dashes with ink edges at the old size;
 *   jar/aim-<s|m|l>           minimize aim: lime dashes with ink edges and four notches.
 * Every ring frame is RING_FRAME logical px square with the band centred on RING_R, so a caller
 * wanting the band centred on radius `R` draws the frame `RING_FRAME · R / RING_R` wide.
 */
import { defineToonArt, getToonArtCanvas } from "../art-registry.js";
import { INK } from "../palette.js";
import { TAU, glint, inkStyle } from "../toon-kit.js";

export var RING_FRAME = 128;
export var RING_R = 52;
/** Rings are drawn up to ~350 px wide round a dragon: bake them at 3× (384 px). */
var RING_SCALE = 3;

/**
 * Line weights per size class, in frame px (a frame px is R / RING_R screen px): the band is
 * thick on small bodies and comparatively thin on big ones, about 4–10 screen px either way.
 */
var CLASSES = {
  s: { band: 13, ink: 4.6 },
  m: { band: 8.5, ink: 3.2 },
  l: { band: 5, ink: 2 },
};

export var MARK_TONES = {
  warn: { band: "#ffa22e", light: "#ffd77a", badge: "#ffb238", badgeShade: "#e07a12" },
  alert: { band: "#ff4436", light: "#ff9a8a", badge: "#ff5243", badgeShade: "#c42a24" },
};

/** Size class for a ring around a body of radius `r` px. */
export function ringClass(r) {
  return r < 34 ? "s" : r < 80 ? "m" : "l";
}

function ringArt(tone, cls) {
  var w = CLASSES[cls];
  return function (c) {
    var cx = RING_FRAME / 2;
    var cy = RING_FRAME / 2;
    var outer = RING_R + w.band / 2;
    var inner = RING_R - w.band / 2;
    // Band: ink-filled ring, then the colour inset by the ink width on both edges.
    c.beginPath();
    c.arc(cx, cy, outer + w.ink / 2, 0, TAU);
    c.arc(cx, cy, Math.max(1, inner - w.ink / 2), 0, TAU, true);
    c.fillStyle = INK;
    c.fill();
    c.beginPath();
    c.arc(cx, cy, outer - w.ink / 2, 0, TAU);
    c.arc(cx, cy, Math.max(1, inner + w.ink / 2), 0, TAU, true);
    c.fillStyle = tone.band;
    c.fill();
    // Lit arc upper-left on the band.
    c.beginPath();
    c.arc(cx, cy, RING_R, Math.PI * 1.08, Math.PI * 1.42);
    c.lineCap = "round";
    c.lineWidth = Math.max(1, w.band - w.ink * 1.6);
    c.strokeStyle = tone.light;
    c.stroke();
  };
}

function dashedRingArt(color, cls, notches) {
  var w = CLASSES[cls];
  return function (c) {
    var cx = RING_FRAME / 2;
    var cy = RING_FRAME / 2;
    var dashes = 12;
    var arc = TAU / dashes;
    var bw = w.band * 0.8;
    for (var pass = 0; pass < 2; pass++) {
      for (var i = 0; i < dashes; i++) {
        var a0 = i * arc + arc * 0.14;
        var a1 = a0 + arc * 0.58;
        c.beginPath();
        c.arc(cx, cy, RING_R, a0, a1);
        if (pass === 0) inkStyle(c, bw + w.ink * 2, INK);
        else inkStyle(c, bw, color);
        c.stroke();
      }
    }
    if (!notches) return;
    // Four aim notches pointing in, outside the ring.
    for (var k = 0; k < 4; k++) {
      var a = k * (TAU / 4) + TAU / 8;
      var r0 = RING_R + w.band * 0.9 + 3;
      var r1 = r0 + 7;
      for (var p = 0; p < 2; p++) {
        c.beginPath();
        c.moveTo(cx + Math.cos(a) * r0, cy + Math.sin(a) * r0);
        c.lineTo(cx + Math.cos(a) * r1, cy + Math.sin(a) * r1);
        if (p === 0) inkStyle(c, 3.4 + w.ink * 1.4, INK);
        else inkStyle(c, 3.4, color);
        c.stroke();
      }
    }
  };
}

/** The "!" badge, 48 logical px: a round sign with an ink edge and a white mark. */
function badgeArt(tone) {
  return function (c) {
    var cx = 24, cy = 24, r = 19;
    c.beginPath();
    c.arc(cx, cy, r, 0, TAU);
    c.fillStyle = tone.badgeShade;
    c.fill();
    c.save();
    c.clip();
    c.beginPath();
    c.arc(cx - 2, cy - 2.5, r, 0, TAU);
    c.fillStyle = tone.badge;
    c.fill();
    c.restore();
    c.beginPath();
    c.arc(cx, cy, r, 0, TAU);
    inkStyle(c, 3.4, INK);
    c.stroke();
    // The mark: a tapered bar and a dot, inked so it pops on any creature.
    function mark(grow, color) {
      c.beginPath();
      c.moveTo(cx - 3.6 - grow, cy - 11 - grow);
      c.lineTo(cx + 3.6 + grow, cy - 11 - grow);
      c.lineTo(cx + 2 + grow, cy + 3 + grow);
      c.lineTo(cx - 2 - grow, cy + 3 + grow);
      c.closePath();
      c.moveTo(cx + 3 + grow, cy + 9);
      c.arc(cx, cy + 9, 3 + grow, 0, TAU);
      c.fillStyle = color;
      c.fill();
    }
    mark(1.6, INK);
    mark(0, "#ffffff");
    glint(c, cx - 9, cy - 9, 3.2);
  };
}

var defined = false;

/** Register the marks (idempotent; the registry draws each on first use). */
export function defineJarMarks() {
  if (defined) return;
  defined = true;
  Object.keys(MARK_TONES).forEach(function (name) {
    var tone = MARK_TONES[name];
    Object.keys(CLASSES).forEach(function (cls) {
      defineToonArt("jar/ring-" + name + "-" + cls, { w: RING_FRAME, h: RING_FRAME, scale: RING_SCALE, name: "ring-" + name + "-" + cls, draw: ringArt(tone, cls) });
    });
    defineToonArt("jar/badge-" + name, { w: 48, h: 48, name: "badge-" + name, draw: badgeArt(tone) });
  });
  Object.keys(CLASSES).forEach(function (cls) {
    defineToonArt("jar/ghost-" + cls, { w: RING_FRAME, h: RING_FRAME, scale: RING_SCALE, name: "ghost-" + cls, draw: dashedRingArt("#ffffff", cls, false) });
    defineToonArt("jar/aim-" + cls, { w: RING_FRAME, h: RING_FRAME, scale: RING_SCALE, name: "aim-" + cls, draw: dashedRingArt("#d9ff5c", cls, true) });
  });
}

/**
 * Every mark's art key (rings of both tones in all three weights, both badges, the ghost and aim
 * rings): the loader warm-up bakes and uploads them all before play.
 * @returns {string[]}
 */
export function jarMarkArtKeys() {
  defineJarMarks();
  var keys = [];
  Object.keys(MARK_TONES).forEach(function (name) {
    Object.keys(CLASSES).forEach(function (cls) {
      keys.push("jar/ring-" + name + "-" + cls);
    });
    keys.push("jar/badge-" + name);
  });
  Object.keys(CLASSES).forEach(function (cls) {
    keys.push("jar/ghost-" + cls);
    keys.push("jar/aim-" + cls);
  });
  return keys;
}

/**
 * @param {"ring"|"badge"|"ghost"|"aim"} kind
 * @param {string} [tone] warn | alert (ring, badge)
 * @param {number} [r] body radius (ring size class)
 * @returns {HTMLCanvasElement|null}
 */
export function getJarMark(kind, tone, r) {
  defineJarMarks();
  if (kind === "badge") return getToonArtCanvas("jar/badge-" + (tone === "alert" ? "alert" : "warn"));
  var cls = ringClass(r || 20);
  if (kind === "ring") return getToonArtCanvas("jar/ring-" + (tone === "alert" ? "alert" : "warn") + "-" + cls);
  return getToonArtCanvas("jar/" + kind + "-" + cls);
}

/** Frame width (px) that puts a ring mark's band on radius `R`. */
export function ringFrameSize(R) {
  return RING_FRAME * R / RING_R;
}
